import { dashboardMgmtService, lookupSvc, dashboardsService, snowflakeService, snowflakeTablesService } from "services/serviceStorage";
import { createAsyncThunk } from '@reduxjs/toolkit';
import { wrapRequest } from "common/utils";
import { BaseApiResponse } from 'common/types';
import {
    GetEmbedUrlParams,
    ReleaseGroup,
    CustomerAccount,
    FetchTablesList,
    CopyDashboardRequest,
    ShareDashboards,
    Table,
    EditebleTableItem
} from './types';
import { Dashboard } from 'store/dashboardViewer/types'

export const getAdminEmbedUrl = createAsyncThunk<string>(
    'dashboardMgmt/getAdminEmbedUrl',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: dashboardMgmtService.getAdminEmbedUrl,
            showNotifications: true
        });

        return data!.result;
    }
);

export const getEmbedUrl = createAsyncThunk<string, GetEmbedUrlParams>(
    'dashboardMgmt/getEmbedUrl',
    async ({ taskId, dashboardId }) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => dashboardMgmtService.getDashboardEmbedUrl(taskId, dashboardId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getReleaseGroups = createAsyncThunk<ReleaseGroup[]>(
    'dashboardMgmt/getReleaseGroups',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<ReleaseGroup[]>>({
            handler: lookupSvc.getTaskReleaseGroups,
            showNotifications: true
        });

        return data!.result;
    }
);

export const getCustomerAccounts = createAsyncThunk<CustomerAccount[]>(
    'dashboardMgmt/getCustomerAccounts',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<CustomerAccount[]>>({
            handler: lookupSvc.getCustomerAccounts,
            showNotifications: true
        });

        return data!.result;
    }
);

export const getSharedDashboards = createAsyncThunk<ShareDashboards[], number[]>(
    'dashboardMgmt/getSharedDashboards',
    async (did) => {
        const { data } = await wrapRequest<BaseApiResponse<ShareDashboards[]>>({
            handler: () => dashboardsService.getSharedDashboards({ did }),
            showNotifications: true
        });

        return data!.result;
    }
);

export const onShareDashboards = createAsyncThunk<void, ShareDashboards[]>(
    'dashboardMgmt/onShareDashboards',
    async (links, { rejectWithValue }) => {
        const errs = await wrapRequest<BaseApiResponse<void>>({
            handler: () => dashboardsService.shareDashboard(links),
            showNotifications: true
        });

        if (errs.errors?.length || errs.warnings?.length || errs.infos?.length) {
            return rejectWithValue(errs);
        }
    }
);

export const onUnShareDashboards = createAsyncThunk<void, ShareDashboards[]>(
    'dashboardMgmt/onUnShareDashboards',
    async (links, { rejectWithValue }) => {
        const errs = await wrapRequest<BaseApiResponse<void>>({
            handler: () => dashboardsService.unshareDashboard(links),
            showNotifications: true
        });

        if (errs.errors?.length || errs.warnings?.length || errs.infos?.length) {
            return rejectWithValue(errs);
        }
    }
);

export const getDashboard = createAsyncThunk<Dashboard, string>(
    'dashboardMgmt/getDashboard',
    async (dashboardId) => {
        const { data } = await wrapRequest<BaseApiResponse<Dashboard>>({
            handler: () => dashboardsService.getDashboard(dashboardId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getTables = createAsyncThunk<{ items: Table[]; totalCount: number }, FetchTablesList>(
    'dashboardMgmt/getTables',
    async (filters) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Table[]; totalCount: number }>>({
            handler: () => snowflakeTablesService.list(filters.filter),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getTable = createAsyncThunk<Table, number>(
    'dashboardMgmt/getTable',
    async (tableId) => {
        const { data } = await wrapRequest<BaseApiResponse<Table>>({
            handler: () => snowflakeTablesService.getTable(tableId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const createTable = createAsyncThunk<void, EditebleTableItem>(
    'dashboardMgmt/createTable',
    async (dashboardTable, { rejectWithValue }) => {
        const errs = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowflakeService.createTable(dashboardTable),
            showNotifications: true
        });

        if (errs.errors?.length || errs.warnings?.length || errs.infos?.length) {
            return rejectWithValue(errs);
        }
    }
);

export const updateTable = createAsyncThunk<void, EditebleTableItem & { tableId: number }>(
    'dashboardMgmt/updateTable',
    async (dashboardTable, { rejectWithValue }) => {
        const errs = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowflakeService.updateTable(dashboardTable),
            showNotifications: true
        });

        if (errs.errors?.length || errs.warnings?.length || errs.infos?.length) {
            return rejectWithValue(errs);
        }
    }
);


export const generateInitialDb = createAsyncThunk<void, number>(
    'dashboardMgmt/generateInitialDb',
    async (releaseGroupId, { rejectWithValue }) => {
        const errs = await wrapRequest<BaseApiResponse<void>>({
            handler: () => snowflakeService.generateInitialDb(releaseGroupId),
            showNotifications: true
        });

        if (errs.errors?.length || errs.warnings?.length || errs.infos?.length) {
            return rejectWithValue(errs);
        }
    }
);

export const getConfiguration = createAsyncThunk<{ appUrl: string }>(
    'dashboardMgmt/getConfiguration',
    async () => {
        const { data } = await wrapRequest<BaseApiResponse<{ appUrl: string }>>({
            handler: snowflakeService.configuration,
            showNotifications: true
        });

        return data!.result;
    }
);

export const copyDashboards = createAsyncThunk<string, CopyDashboardRequest>(
    'dashboardMgmt/copyDashboards',
    async (request) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => dashboardsService.copyDashboards(request),
            showNotifications: true
        });

        return data!.result;
    }
);

export const syncDashboards = createAsyncThunk<void, number[]>(
    'dashboardMgmt/syncDashboards',
    async (releaseGroupIds, { rejectWithValue }) => {
        const errs = await wrapRequest<BaseApiResponse<void>>({
            handler: () => dashboardsService.syncDashboards(releaseGroupIds),
            showNotifications: true
        });

        if (errs.errors?.length || errs.warnings?.length || errs.infos?.length) {
            return rejectWithValue(errs);
        }
    }
);